import { useEffect, useState } from "react";

/**
 * Theme switching (HANDOFF §1): `.dark` on <html>, from the user's preference, defaulting to prefers-color-scheme.
 * The preference is persisted in localStorage under `iga.theme`.
 */
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "iga.theme";
const listeners = new Set<() => void>();
let media: MediaQueryList | null = null;
let current: ThemePreference = "system";

function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function setClass(theme: ResolvedTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.style.colorScheme = theme;
}

function onMediaChange() {
  if (current === "system") { setClass(systemTheme()); listeners.forEach((l) => l()); }
}

/** The stored preference (or "system" when nothing is stored). */
export function getThemePreference(): ThemePreference {
  try {
    const v = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch { /* storage blocked */ }
  return "system";
}

/** The theme that is actually shown right now. */
export function resolvedTheme(pref: ThemePreference = current): ResolvedTheme {
  return pref === "system" ? systemTheme() : pref;
}

/**
 * Sets `.dark` on <html> according to `pref`, persists it, and – for "system" – follows prefers-color-scheme.
 * Call once at start-up with `getThemePreference()` (or use `initTheme()`), then whenever the user changes it.
 */
export function applyTheme(pref: ThemePreference): void {
  current = pref;
  try { localStorage.setItem(STORAGE_KEY, pref); } catch { /* storage blocked */ }
  if (typeof window !== "undefined" && window.matchMedia && !media) {
    media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", onMediaChange);
  }
  setClass(resolvedTheme(pref));
  listeners.forEach((l) => l());
}

/** Applies the stored preference. Safe to call before React renders (avoids a light flash). */
export function initTheme(): ThemePreference {
  const pref = getThemePreference();
  applyTheme(pref);
  return pref;
}

/** React hook: current preference and resolved theme, re-rendering on change (including OS changes). */
export function useTheme(): { preference: ThemePreference; resolved: ResolvedTheme; setPreference: (p: ThemePreference) => void } {
  const [, tick] = useState(0);
  useEffect(() => {
    const l = () => tick((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return { preference: current, resolved: resolvedTheme(current), setPreference: applyTheme };
}
